import {
  Button,
  Code,
  Flex,
  Skeleton,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { Form, Formik } from "formik";
import { InputControl } from "formik-chakra-ui";
import { QRCodeSVG } from "qrcode.react";
import React from "react";
import {
  RiAddCircleLine,
  RiDeleteBin6Line,
  RiShieldLine,
  RiSmartphoneLine,
} from "react-icons/ri";
import { ConnectionTag } from "src/components/ConnectionTag";
import { GenericError } from "src/components/Feedback/GenericError";
import {
  AdminCard,
  AdminCardFormButtons,
} from "src/components/Layout/AdminCard";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { CopyToClipboard } from "src/components/Text/CopyToClipboard";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import {
  Factor,
  NeedReauthenticateError,
  OtpEnrollment,
  useMfaApi,
} from "src/hooks/useMfaApi";
import { validateWithZod } from "src/services/formValidations";
import { isNullOrUndefined } from "src/services/predicates";
import * as RD from "src/types/remoteData";
import { z } from "zod";

type Props = {
  factor: Factor | undefined;
  refreshFactors: () => Promise<void>;
};

export const OtpFactorCard: React.FC<Props> = ({ factor, refreshFactors }) => {
  const isActive = factor?.active ?? false;
  const { onOpen, onClose, isOpen } = useDisclosure();
  const toast = useAvelaToast();
  const mfaApi = useMfaApi();
  const [state, setState] = React.useState<"view" | "enroll">("view");

  const [otpEnrollmentRD, setOtpEnrollmentRD] = React.useState<
    RD.RemoteData<unknown, OtpEnrollment>
  >(RD.notAsked());

  const connectApp = React.useCallback(async () => {
    onOpen();
    setOtpEnrollmentRD(RD.loading());
    try {
      const enrollment = await mfaApi.enrollOtp();
      setOtpEnrollmentRD(RD.success(enrollment));
    } catch (err) {
      if (err instanceof NeedReauthenticateError) {
        window.location.reload();
      } else {
        setOtpEnrollmentRD(RD.notAsked());
        throw err;
      }
    }
  }, [mfaApi, onOpen]);

  const [isDeleting, setDeleting] = React.useState(false);
  const deleteConfirmation = useConfirmationDialog({
    header: "Remove authenticator app?",
    body: "You will no longer be able to use verification codes from this app. You can connect it again later.",
    confirmButton: {
      label: "Yes, remove",
    },
  });

  const handleRemove = React.useCallback(async () => {
    if (!factor || !(await deleteConfirmation.confirm())) {
      return;
    }

    setDeleting(true);
    try {
      await mfaApi.deleteFactor(factor.id);
      await refreshFactors();
      setState("view");
      toast({
        title: "Authenticator app removed",
        status: "success",
      });
      setDeleting(false);
    } catch (error) {
      if (error instanceof NeedReauthenticateError) {
        window.location.reload();
      } else {
        console.error(error);
        toast.error({
          title: "Unable to remove authenticator app",
        });
        setDeleting(false);
      }
    }
  }, [deleteConfirmation, factor, mfaApi, refreshFactors, toast]);

  const [isLoading, setIsLoading] = React.useState(false);
  const onManage = React.useCallback(async () => {
    setIsLoading(true);
    try {
      if (!(await mfaApi.hasMfaToken())) {
        window.location.reload();
        return;
      }
      onOpen();
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      toast.genericError();
      setIsLoading(false);
    }
  }, [mfaApi, onOpen, toast]);

  return (
    <>
      {deleteConfirmation.confirmationDialog}
      <AdminCard
        icon={RiSmartphoneLine}
        variant="collapsible"
        collapse={!isOpen}
        title={
          <Flex gap="2" alignItems="center">
            Authenticator app
            <ConnectionTag isConnected={isActive} />
          </Flex>
        }
        description="Receive verification codes via Google Authenticator"
        actions={
          isOpen ? null : (
            <Button
              leftIcon={<RiShieldLine />}
              variant="outline"
              colorScheme="gray"
              isLoading={isLoading}
              loadingText="Manage"
              onClick={onManage}
            >
              Manage
            </Button>
          )
        }
      >
        {state === "view" && isNullOrUndefined(factor) ? (
          <>
            <Flex direction="row" gap="4" justifyContent="space-between">
              <Flex direction="column" gap="1">
                <Text>No authenticator app connected</Text>
                <Text color="blackAlpha.600">
                  Connect an authenticator app to generate verification codes on
                  your device.
                </Text>
              </Flex>

              <Button
                colorScheme="gray"
                leftIcon={<RiAddCircleLine />}
                isLoading={otpEnrollmentRD.isLoading()}
                loadingText="Connecting app"
                onClick={async () => {
                  try {
                    await connectApp();
                    setState("enroll");
                  } catch (error) {
                    if (error instanceof NeedReauthenticateError) {
                      window.location.reload();
                    }
                    console.error(error);
                    toast.error({
                      title: "Unable to connect authenticator app",
                    });
                  }
                }}
                size="sm"
                variant="outline"
              >
                Connect app
              </Button>
            </Flex>

            <AdminCardFormButtons>
              <Button size="sm" variant="ghost" onClick={onClose}>
                Close
              </Button>
            </AdminCardFormButtons>
          </>
        ) : null}
        {state === "view" && !isNullOrUndefined(factor) ? (
          <>
            <Flex direction="row" gap="4" justifyContent="space-between">
              <Flex direction="column" gap="1">
                <Text>Authenticator app connected</Text>
                <Text color="blackAlpha.600">
                  Generates verification codes on your device.
                </Text>
              </Flex>

              <Button
                colorScheme="gray"
                isLoading={isDeleting}
                leftIcon={<RiDeleteBin6Line />}
                loadingText="Removing..."
                onClick={handleRemove}
                size="sm"
                variant="outline"
              >
                Remove
              </Button>
            </Flex>

            <AdminCardFormButtons>
              <Button
                isDisabled={isDeleting}
                onClick={onClose}
                size="sm"
                variant="ghost"
              >
                Close
              </Button>
            </AdminCardFormButtons>
          </>
        ) : null}

        {state === "enroll" ? (
          <>
            <RemoteDataView
              remoteData={otpEnrollmentRD}
              error={(error) => <GenericError error={error} />}
              loading={<Loading />}
            >
              {(enrollment) => {
                return (
                  <Flex direction="column" gap="6">
                    <Formik<FormikType>
                      initialValues={{ verificationCode: "" }}
                      validate={validateWithZod(FormikSchema)}
                      onSubmit={async (values) => {
                        try {
                          await mfaApi.confirmOtp(values.verificationCode);
                          await refreshFactors();
                          toast({
                            title: "Authenticator app connected",
                            status: "success",
                          });
                          setState("view");
                        } catch (error) {
                          if (error instanceof NeedReauthenticateError) {
                            window.location.reload();
                          }
                          console.error(error);
                          toast.error({
                            title: "Error activating authenticator app",
                          });
                        }
                      }}
                    >
                      {(formik) => (
                        <Flex as={Form} direction="column" gap="4">
                          <Text>
                            Scan this QR code with Google Authenticator or
                            another authenticator app.
                          </Text>
                          <QRCodeSVG
                            value={enrollment.barcode_uri}
                            width="250px"
                            height="250px"
                          />
                          <Text>
                            Can't scan it? Enter this setup key manually:{" "}
                            <CopyToClipboard text={enrollment.secret}>
                              <Code>{enrollment.secret}</Code>
                            </CopyToClipboard>
                          </Text>
                          <InputControl
                            maxWidth="20rem"
                            label="Verification code"
                            name="verificationCode"
                            inputProps={{
                              inputMode: "numeric",
                              autoComplete: "one-time-code",
                              maxLength: 6,
                              pattern: "[0-9]*",
                              placeholder: "6-digit code",
                            }}
                          />

                          <AdminCardFormButtons>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                formik.resetForm();
                                setState("view");
                              }}
                              isDisabled={formik.isSubmitting}
                            >
                              Cancel
                            </Button>
                            <Button
                              type="submit"
                              size="sm"
                              isLoading={formik.isSubmitting}
                              loadingText="Activating..."
                            >
                              Activate
                            </Button>
                          </AdminCardFormButtons>
                        </Flex>
                      )}
                    </Formik>
                  </Flex>
                );
              }}
            </RemoteDataView>
          </>
        ) : null}
      </AdminCard>
    </>
  );
};

const Loading = () => {
  return (
    <Flex direction="column" gap="4">
      <Skeleton maxWidth="20rem" height="2rem" />
      <Skeleton maxWidth="250px" height="250px" />
      <Skeleton maxWidth="40rem" height="2rem" />
      <Skeleton maxWidth="15rem" height="2rem" />
    </Flex>
  );
};

const FormikSchema = z.object({
  verificationCode: z
    .string()
    .regex(/^\d{6}$/, "Enter the six-digit code from your authenticator app."),
});
type FormikType = z.infer<typeof FormikSchema>;
